import React, { useState, useEffect } from 'react';
import API_URL from './config';
import './api'; // Initialize fetch interceptor for session expiry handling
import './Login.css';
import './App.css';
import Schedule from './Schedule';
import './Schedule.css';
import MyBookings from './MyBookings';
import './MyBookings.css';
import AdminBookings from './AdminBookings';
import './AdminBookings.css';
import AdminBlackouts from './AdminBlackouts';
import AdminAnalytics from './AdminAnalytics';
import AdminAuditLog from './AdminAuditLog';
import AdminCustomers from './AdminCustomers';
import AdminPricing from './AdminPricing';
import AdminInvoices from './AdminInvoices';
import AdminSettings from './AdminSettings';
import AdminCalendar from './AdminCalendar';
import MyInvoices from './MyInvoices';
import './AdminFeatures.css';
import UserProfile from './UserProfile';
import LandingPage from './LandingPage';
import './LandingPage.css';

// Contact Us Modal Component
function ContactUsModal({ user, token, onClose }) {
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [organization, setOrganization] = useState(user?.organization || '');
  const [email, setEmail] = useState(user?.email || '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !message) {
      setError('Please enter your email and message.');
      return;
    }
    setSending(true);
    setError('');
    setSuccess('');
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ firstName, lastName, organization, email, message })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send message');
      setSuccess('Message sent successfully! We will get back to you soon.');
      setMessage('');
    } catch (err) {
      setError(err.message);
    }
    setSending(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={e => e.stopPropagation()}>
        <h2>Contact Us</h2>
        <p className="contact-subtitle">We'd love to hear from you!</p>
        
        {success && <div className="success">{success}</div>}
        {error && <div className="error">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} />
            </div>
          </div>
          
          <div className="form-group">
            <label>Organization</label>
            <input type="text" value={organization} onChange={e => setOrganization(e.target.value)} />
          </div>
          
          <div className="form-group">
            <label>Email (for reply) *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="Enter your preferred email for a reply" />
          </div>
          
          <div className="form-group">
            <label>Message *</label>
            <textarea 
              value={message} 
              onChange={e => setMessage(e.target.value)} 
              required 
              rows="5" 
              placeholder="How can we help you?"
            />
          </div>
          
          <div className="modal-actions">
            {success ? (
              <button type="button" className="submit-btn" onClick={onClose}>Close</button>
            ) : (
              <>
                <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                <button type="submit" className="submit-btn" disabled={sending}>
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// Helper to get stored user from localStorage
function getStoredUser() {
  try {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function App() {
  const [user, setUser] = useState(getStoredUser);
  const [showLanding, setShowLanding] = useState(() => !getStoredUser());
  const [registerMode, setRegisterMode] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [bookingsRefresh, setBookingsRefresh] = useState(0);
  const [adminTab, setAdminTab] = useState(() => {
    const stored = localStorage.getItem('adminTab');
    return stored || 'requests';
  });
  const [showProfile, setShowProfile] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);

  // Handle calendar date click - navigate to schedule
  const handleCalendarDateClick = (dateStr) => {
    setScheduleDate(dateStr);
    setAdminTab('schedule');
  };

  // Persist adminTab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('adminTab', adminTab);
  }, [adminTab]);

  // Persist user to localStorage when it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  // Handle logout (can be called from anywhere via event)
  const handleLogout = () => {
    setUser(null);
    setLoginError('Your session has expired. Please log in again.');
  };

  // Listen for session expired events from API calls
  useEffect(() => {
    const handleSessionExpired = () => handleLogout();
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, []);

  // Close admin menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showAdminMenu && !e.target.closest('.admin-menu-wrapper')) {
        setShowAdminMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showAdminMenu]);

  // Registration fields
  const [reg, setReg] = useState({
    username: '', password: '',
    first_name: '', last_name: '', email: '', phone: '', address: '', organization: '',
    email_reminders_enabled: true,
    terms_accepted: false
  });
  const [showTerms, setShowTerms] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  // Login fields
  const [login, setLogin] = useState({ username: '', password: '' });
  
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  
  // Password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeError, setChangeError] = useState('');
  
  // Email verification state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verificationSuccess, setVerificationSuccess] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(login)
      });
      const data = await res.json();
      if (!res.ok) {
        // Check if email verification is required
        if (data.requiresVerification && data.email) {
          setVerificationEmail(data.email);
          setShowVerification(true);
          return;
        }
        throw new Error(data.error || 'Login failed');
      }
      
      // Check if user must change password
      if (data.mustChangePassword) {
        setTempToken(data.token);
        setTempUser(data.user);
        setMustChangePassword(true);
        setLogin({ ...login, password: '' });
      } else {
        setUser(data);
      }
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to process request');
      
      setForgotMessage(data.message);
      setForgotEmail('');
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangeError('');
    
    if (newPassword.length < 6) {
      setChangeError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setChangeError('Passwords do not match');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({ newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');
      
      // Password changed successfully, complete login
      setUser({ token: tempToken, user: tempUser });
      setMustChangePassword(false);
      setTempToken(null);
      setTempUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setChangeError(err.message);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');
    
    if (!reg.terms_accepted) {
      setRegisterError('You must accept the Terms & Conditions to register.');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...reg, isAdmin: false })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Registration failed');
      
      // If registration requires verification, show verification screen
      if (data.requiresVerification) {
        setVerificationEmail(reg.email);
        setShowVerification(true);
        setRegisterMode(false);
        setReg({ username: '', password: '', first_name: '', last_name: '', email: '', phone: '', address: '', organization: '', email_reminders_enabled: true, terms_accepted: false });
      } else {
        setRegisterSuccess('Registration successful! You can now log in.');
        setRegisterMode(false);
        setLoginError('');
        setLogin({ username: reg.username, password: '' });
        setReg({ username: '', password: '', first_name: '', last_name: '', email: '', phone: '', address: '', organization: '', email_reminders_enabled: true, terms_accepted: false });
      }
    } catch (err) {
      setRegisterError(err.message);
    }
  };

  // Handle email verification
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setVerificationError('');
    setVerificationSuccess('');
    setVerificationLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail, code: verificationCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      
      setVerificationSuccess('Email verified successfully! You can now log in.');
      setTimeout(() => {
        setShowVerification(false);
        setVerificationCode('');
        setVerificationEmail('');
        setVerificationSuccess('');
        setRegisterSuccess('Email verified! You can now log in.');
      }, 2000);
    } catch (err) {
      setVerificationError(err.message);
    }
    setVerificationLoading(false);
  };
  
  // Resend verification code
  const handleResendVerification = async () => {
    setVerificationError('');
    setVerificationSuccess('');
    setVerificationLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to resend code');
      
      setVerificationSuccess('A new verification code has been sent to your email.');
    } catch (err) {
      setVerificationError(err.message);
    }
    setVerificationLoading(false);
  };

  // Show landing page if not logged in and showLanding is true
  if (showLanding && !user) {
    return (
      <LandingPage 
        onGetStarted={() => {
          setShowLanding(false);
          setRegisterMode(true);
        }}
        onLogin={() => {
          setShowLanding(false);
          setRegisterMode(false);
        }}
      />
    );
  }

  return (
    <div className="centered-page">
      <div className="title">
        <span className="title-the">The</span><span className="title-turf">Turf</span>
        <span className="title-at-gsc">at GSC</span>
        <span className="title-scheduler">Scheduler</span>
      </div>
      {user ? (
        <>
          <div className="user-bar glass">
            <div className="user-bar-left">
              {user.user.isAdmin && (
                <div className="admin-menu-wrapper">
                  <button 
                    className="admin-menu-btn glass-btn"
                    onClick={() => setShowAdminMenu(!showAdminMenu)}
                  >
                    <span className="menu-icon">☰</span>
                    Menu
                  </button>
                  {showAdminMenu && (
                    <div className="admin-menu-dropdown-list glass-dropdown">
                      <button onClick={() => { setAdminTab('requests'); setShowAdminMenu(false); }} className={adminTab === 'requests' ? 'active' : ''}>Manage Requests</button>
                      <button onClick={() => { setAdminTab('schedule'); setShowAdminMenu(false); }} className={adminTab === 'schedule' ? 'active' : ''}>Schedule</button>
                      <button onClick={() => { setAdminTab('customers'); setShowAdminMenu(false); }} className={adminTab === 'customers' ? 'active' : ''}>Customers</button>
                      <button onClick={() => { setAdminTab('blackouts'); setShowAdminMenu(false); }} className={adminTab === 'blackouts' ? 'active' : ''}>Blackouts</button>
                      <button onClick={() => { setAdminTab('analytics'); setShowAdminMenu(false); }} className={adminTab === 'analytics' ? 'active' : ''}>Analytics</button>
                      <button onClick={() => { setAdminTab('audit'); setShowAdminMenu(false); }} className={adminTab === 'audit' ? 'active' : ''}>Audit Log</button>
                      <button onClick={() => { setAdminTab('pricing'); setShowAdminMenu(false); }} className={adminTab === 'pricing' ? 'active' : ''}>Pricing</button>
                      <button onClick={() => { setAdminTab('invoices'); setShowAdminMenu(false); }} className={adminTab === 'invoices' ? 'active' : ''}>Invoices</button>
                      <button onClick={() => { setAdminTab('settings'); setShowAdminMenu(false); }} className={adminTab === 'settings' ? 'active' : ''}>Settings</button>
                    </div>
                  )}
                </div>
              )}
              <p className="user-welcome">Welcome, <strong>{user.user.firstName || user.user.username}</strong>{user.user.isAdmin && ' (Admin)'}</p>
              {user.user.isAdmin && (
                <div className="quick-access">
                  <button 
                    className={`quick-btn ${adminTab === 'schedule' ? 'active' : ''}`}
                    onClick={() => setAdminTab('schedule')}
                  >
                    📅 Schedule
                  </button>
                  <button 
                    className={`quick-btn ${adminTab === 'requests' ? 'active' : ''}`}
                    onClick={() => setAdminTab('requests')}
                  >
                    📋 Requests
                  </button>
                </div>
              )}
            </div>
            <div className="user-bar-actions">
              <button className="glass-btn" onClick={() => setShowContact(true)}>Contact Us</button>
              <button className="glass-btn" onClick={() => setShowProfile(true)}>Edit Profile</button>
              <button className="glass-btn logout" onClick={() => { setUser(null); setShowLanding(true); }}>Logout</button>
            </div>
          </div>
          {showContact && (
            <ContactUsModal 
              user={{
                firstName: user.user.firstName,
                lastName: user.user.lastName,
                organization: user.user.organization,
                email: user.user.email
              }}
              token={user.token}
              onClose={() => setShowContact(false)}
            />
          )}
          {showProfile && (
            <UserProfile 
              token={user.token} 
              onClose={() => setShowProfile(false)}
              onUpdate={(data) => {
                setUser(prev => ({
                  ...prev,
                  user: { ...prev.user, firstName: data.first_name }
                }));
              }}
            />
          )}
          {user.user.isAdmin && (
            <>
              {adminTab === 'schedule' && <AdminCalendar token={user.token} onDateSelect={handleCalendarDateClick} />}
              {adminTab === 'requests' && (
                <AdminBookings token={user.token} refreshKey={bookingsRefresh} onBookingChange={() => setBookingsRefresh(r => r + 1)} />
              )}
              {adminTab === 'schedule' && (
                <Schedule token={user.token} refreshKey={bookingsRefresh} onBookingChange={() => setBookingsRefresh(r => r + 1)} isAdmin={user.user.isAdmin} initialDate={scheduleDate} />
              )}
              {adminTab === 'timeslot' && (
                <AdminBookings token={user.token} refreshKey={bookingsRefresh} onBookingChange={() => setBookingsRefresh(r => r + 1)} viewMode="timeslot" />
              )}
              {adminTab === 'customers' && (
                <AdminCustomers token={user.token} />
              )}
              {adminTab === 'blackouts' && (
                <AdminBlackouts token={user.token} />
              )}
              {adminTab === 'analytics' && (
                <AdminAnalytics token={user.token} />
              )}
              {adminTab === 'audit' && (
                <AdminAuditLog token={user.token} />
              )}
              {adminTab === 'pricing' && (
                <AdminPricing token={user.token} />
              )}
              {adminTab === 'invoices' && (
                <AdminInvoices token={user.token} />
              )}
              {adminTab === 'settings' && (
                <AdminSettings token={user.token} />
              )}
            </>
          )}
          {!user.user.isAdmin && (
            <>
              <Schedule token={user.token} refreshKey={bookingsRefresh} onBookingChange={() => setBookingsRefresh(r => r + 1)} isAdmin={false} />
              <MyBookings token={user.token} refreshKey={bookingsRefresh} />
              <MyInvoices token={user.token} />
            </>
          )}
          
          {/* Footer with logo */}
          <footer className="app-footer">
            <span>© {new Date().getFullYear()} DW Enterprises LLC. All rights reserved.</span>
          </footer>
        </>
      ) : (
        <div className="login-container">
          {mustChangePassword ? (
            <form onSubmit={handleChangePassword}>
              <h2>Change Password</h2>
              <p className="change-password-info">
                You must change your temporary password before continuing.
              </p>
              {changeError && <div className="error">{changeError}</div>}
              <input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                minLength={6}
              />
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
              <button type="submit">Change Password</button>
              <button 
                type="button" 
                className="link-btn"
                onClick={() => {
                  setMustChangePassword(false);
                  setTempToken(null);
                  setTempUser(null);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
              >
                Cancel
              </button>
            </form>
          ) : showForgotPassword ? (
            <form onSubmit={handleForgotPassword}>
              <h2>Forgot Password</h2>
              <p className="forgot-password-info">
                Enter your email address and we'll send you a temporary password.
              </p>
              {forgotError && <div className="error">{forgotError}</div>}
              {forgotMessage && <div className="success">{forgotMessage}</div>}
              <input
                type="email"
                placeholder="Email Address"
                value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                required
              />
              <button type="submit" disabled={forgotLoading}>
                {forgotLoading ? 'Sending...' : 'Send Temporary Password'}
              </button>
              <button 
                type="button" 
                className="link-btn"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotEmail('');
                  setForgotError('');
                  setForgotMessage('');
                }}
              >
                Back to Login
              </button>
            </form>
          ) : showVerification ? (
            <form onSubmit={handleVerifyEmail}>
              <h2>Verify Your Email</h2>
              <p className="verification-info">
                We've sent a 6-digit verification code to <strong>{verificationEmail}</strong>
              </p>
              {verificationError && <div className="error">{verificationError}</div>}
              {verificationSuccess && <div className="success">{verificationSuccess}</div>}
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '24px' }}
              />
              <button type="submit" disabled={verificationLoading || verificationCode.length !== 6}>
                {verificationLoading ? 'Verifying...' : 'Verify Email'}
              </button>
              <button 
                type="button" 
                className="link-btn"
                onClick={handleResendVerification}
                disabled={verificationLoading}
              >
                Resend Code
              </button>
              <button 
                type="button" 
                className="link-btn"
                onClick={() => {
                  setShowVerification(false);
                  setVerificationCode('');
                  setVerificationEmail('');
                  setVerificationError('');
                  setVerificationSuccess('');
                }}
              >
                Back to Login
              </button>
            </form>
          ) : !registerMode ? (
            <form onSubmit={handleLogin}>
              <h2>Login</h2>
              {loginError && <div className="error">{loginError}</div>}
              <input
                type="text"
                placeholder="Username"
                value={login.username}
                onChange={e => setLogin({ ...login, username: e.target.value })}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={login.password}
                onChange={e => setLogin({ ...login, password: e.target.value })}
                required
              />
              <button type="submit">Login</button>
              <button type="button" className="link-btn" onClick={() => setRegisterMode(true)}>
                Need an account? Register
              </button>
              <button 
                type="button" 
                className="forgot-password-btn"
                onClick={() => setShowForgotPassword(true)}
              >
                Forgot Password?
              </button>
              <button 
                type="button" 
                className="forgot-password-btn"
                onClick={() => setShowContact(true)}
              >
                Contact Us
              </button>
              {registerSuccess && <div className="success">{registerSuccess}</div>}
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              <h2>Register</h2>
              {registerError && <div className="error">{registerError}</div>}
              <input
                type="text"
                placeholder="Username"
                value={reg.username}
                onChange={e => setReg({ ...reg, username: e.target.value })}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={reg.password}
                onChange={e => setReg({ ...reg, password: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="First Name"
                value={reg.first_name}
                onChange={e => setReg({ ...reg, first_name: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Last Name"
                value={reg.last_name}
                onChange={e => setReg({ ...reg, last_name: e.target.value })}
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={reg.email}
                onChange={e => setReg({ ...reg, email: e.target.value })}
                required
              />
              <input
                type="tel"
                placeholder="Phone"
                value={reg.phone}
                onChange={e => setReg({ ...reg, phone: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Address"
                value={reg.address}
                onChange={e => setReg({ ...reg, address: e.target.value })}
                required
              />
              <input
                type="text"
                placeholder="Organization"
                value={reg.organization}
                onChange={e => setReg({ ...reg, organization: e.target.value })}
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={reg.email_reminders_enabled}
                  onChange={e => setReg({ ...reg, email_reminders_enabled: e.target.checked })}
                />
                Send me email reminders for upcoming bookings
              </label>
              <label className="checkbox-label terms-checkbox">
                <input
                  type="checkbox"
                  checked={reg.terms_accepted}
                  onChange={e => setReg({ ...reg, terms_accepted: e.target.checked })}
                  required
                />
                I agree to the{' '}
                <button
                  type="button"
                  className="terms-link"
                  onClick={async () => {
                    if (!termsContent) {
                      try {
                        const res = await fetch('/terms-and-conditions.txt');
                        const text = await res.text();
                        setTermsContent(text);
                      } catch (err) {
                        setTermsContent('Unable to load Terms & Conditions. Please try again later.');
                      }
                    }
                    setShowTerms(true);
                  }}
                >
                  Terms & Conditions
                </button>
              </label>
              <button type="submit">Register</button>
              <button type="button" className="link-btn" onClick={() => setRegisterMode(false)}>
                Already have an account? Login
              </button>
            </form>
          )}
          {showContact && (
            <ContactUsModal 
              user={null}
              token={null}
              onClose={() => setShowContact(false)}
            />
          )}
          {showTerms && (
            <div className="modal-overlay" onClick={() => setShowTerms(false)}>
              <div className="modal terms-modal" onClick={e => e.stopPropagation()}>
                <h2>Terms & Conditions</h2>
                <div className="terms-content">
                  <pre>{termsContent}</pre>
                </div>
                <div className="modal-actions">
                  <button onClick={() => setShowTerms(false)}>Close</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default App;
